// src/components/Credit/Credit.jsx

// Main component for Credit Management page

import React, { useState, useEffect } from 'react';
import creditService from '../../services/creditService';
import CreditTable from './CreditTable';
import CreditModal from './CreditModal';
import { showNotification } from '../../utils/notification';
import ConfirmDialog from "../common/ConfirmDialog";
import './credit.css';

/**
 * Credit Component
 * Main page component that manages credits
 * Handles CRUD operations and filtering
 * 
 * @param {Object} props - Component props
 * @param {Function} props.toggleSidebar - Function to toggle sidebar
 * @param {Object} props.currentUser - Current logged in user object
 * @param {string} props.currentUser.role - User role (supadmin or admin)
 * @param {number} props.currentUser.id - User ID
 */
const Credit = ({ toggleSidebar, currentUser }) => {
  // ========== STATE MANAGEMENT ==========
  // All credits from the backend
  const [credits, setCredits] = useState([]);
  // Filtered credits (after applying search/filters)
  const [filteredCredits, setFilteredCredits] = useState([]);
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  // Saving state (for add/edit operations)
  const [isSaving, setIsSaving] = useState(false);
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Currently selected credit for editing (null for add mode)
  const [selectedCredit, setSelectedCredit] = useState(null);
  // Filter values
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    startDate: '',
    endDate: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Get user role and ID - FIX: Don't default to 'admin', use actual role
  const userRole = currentUser?.role;
  const userId = currentUser?.id;

  // Debug log to see what we're receiving
  console.log('Credit Component - User Info:', { userRole, userId, currentUser });

  const initialFormData = {
    name: '',
    amount: '',
    status: 'pending',
    date: ''
  };

  // Delete confirmation modal
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [creditToDelete, setCreditToDelete] = useState(null);
  // Optional: Loading state while deleting
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (credit) => {
    setCreditToDelete(credit);
    setIsConfirmOpen(true);
  };

  // ========== LOAD DATA ON COMPONENT MOUNT ==========
  /**
   * Load all credits from the backend when component first renders
   * This runs once when the component mounts
   */
  useEffect(() => {
    // Only load if we have user role information
    if (userRole) {
      loadCredits();
    }
  }, [currentPage, userRole, userId]); // Reload when page, role, or userId changes

  /**
   * Apply filters whenever credits or filter values change
   */
  useEffect(() => {
    applyFilters();
  }, [credits, filters]); // Run whenever credits or filters change

  // ========== DATA LOADING ==========
  /**
   * Fetch all credits from the backend based on user role
   */
  const loadCredits = async () => {
    try {
      setIsLoading(true);
      
      // Validate we have the necessary information
      if (!userRole) {
        throw new Error('User role is not available');
      }
      
      // Call the API service with pagination and user role
      const data = await creditService.getAll(currentPage, itemsPerPage, userRole, userId);
      // Update state with fetched data
      setCredits(data);
      // Show success message
      const roleMessage = userRole === 'supadmin' ? 'all' : 'your';
      showNotification(`Loaded ${data.length} ${roleMessage} credits`, 'success');
    } catch (error) {
      console.error('Error loading credits:', error);
      showNotification(`Failed to load credits: ${error.message}`, 'error');
      // Set empty array on error
      setCredits([]);
    } finally {
      // Always stop loading, whether successful or not
      setIsLoading(false);
    }
  };

  // ========== FILTERING ==========
  /**
   * Apply current filters to the credit list
   */
  const applyFilters = () => {
    const safeCredits = Array.isArray(credits) ? credits : [];
    let filtered = [...safeCredits];

    // Filter by status if selected
    if (filters.status) {
      filtered = filtered.filter(cred => cred.status === filters.status);
    }

    // Filter by search term (searches name)
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(cred => {
        const name = cred.name?.toLowerCase() || '';
        return name.includes(searchLower);
      });
    }

    // Filter by date range
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(cred => {
        if (!cred.date) return false;
        const creditDate = new Date(cred.date);
        creditDate.setHours(0, 0, 0, 0);
        return creditDate >= startDate;
      });
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(cred => {
        if (!cred.date) return false;
        const creditDate = new Date(cred.date);
        return creditDate <= endDate;
      });
    }

    setFilteredCredits(filtered);
  };

  /**
   * Handle filter input changes
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      startDate: '',
      endDate: ''
    });
    showNotification('Filters cleared', 'info');
  };

  // ========== MODAL OPERATIONS ==========
  /**
   * Open modal for adding new credit
   */
  const handleAddNew = () => {
    setSelectedCredit(null);
    setIsModalOpen(true);
  };

  /**
   * Open modal for editing existing credit
   */
  const handleEdit = (credit) => {
    setSelectedCredit(credit);
    setIsModalOpen(true);
  };

  /**
   * Close the modal
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCredit(null);
  };

  // ========== CRUD OPERATIONS ==========
  /**
   * Save credit (create or update)
   * @param {Object} formData - The form data from the modal
   */
  const handleSave = async (formData) => {
    try {
      setIsSaving(true);

      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
      };

      if (selectedCredit) {
        await creditService.update(selectedCredit.id, payload);
        showNotification(
          `Credit "${payload.name}" updated successfully!`,
          'success'
        );
      } else {
        await creditService.create(payload);
        showNotification(
          `New credit "${payload.name}" added successfully!`,
          'success'
        );
      }

      await loadCredits();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving credit:', error);
      showNotification(
        `Failed to save: ${error.message}`,
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Delete a credit
   */
  const confirmDelete = async () => {
    if (!creditToDelete) return;

    try {
      setIsDeleting(true);
      await creditService.delete(creditToDelete.id);
      showNotification("Credit deleted successfully", "success");
      await loadCredits();
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification(`Failed to delete: ${error.message}`, "error");
    } finally {
      setIsDeleting(false);
      setIsConfirmOpen(false);
      setCreditToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsConfirmOpen(false);
    setCreditToDelete(null);
  };

  // Show loading message if user info not available
  if (!userRole) {
    return (
      <div className="credit-main-content">
        <div className="credit-loading">Loading user information...</div>
      </div>
    );
  }

  // ========== RENDER ==========
  return (
    <div className="credit-main-content">
      {/* Page Header */}
      <div className="credit-content-header">
        <div>
          <h1 className="credit-content-title">Credit Management</h1>
          {userRole === 'admin' && (
            <p className="credit-user-info">Viewing your credits</p>
          )}
          {userRole === 'supadmin' && (
            <p className="credit-user-info">Viewing all credits (Super Admin)</p>
          )}
        </div>
        <button 
          className="credit-btn credit-btn-add" 
          onClick={handleAddNew}
        >
          + Add New Credit
        </button>
      </div>

      {/* Filter Controls */}
      <div className="credit-filter-controls">
        <div className="credit-filter-group">
          {/* Search Input */}
          <input
            type="text"
            name="search"
            placeholder="Search by name..."
            value={filters.search}
            onChange={handleFilterChange}
            className="credit-custom-input credit-search-input"
          />

          {/* Status Filter */}
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="credit-custom-select"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          {/* Start Date Filter */}
          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
            className="credit-custom-input credit-date-input"
            placeholder="Start Date"
          />

          {/* End Date Filter */}
          <input
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
            className="credit-custom-input credit-date-input"
            placeholder="End Date"
          />

          {/* Clear Filters Button */}
          <button 
            className="credit-btn credit-btn-clear" 
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Data Table */}
      <CreditTable
        credits={filteredCredits}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        isLoading={isLoading}
      />

      {/* Add/Edit Modal */}
      <CreditModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        credit={selectedCredit}
        isSaving={isSaving}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete Credit"
        message={`Are you sure you want to delete "${creditToDelete?.name}"?`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isProcessing={isDeleting}
      />
    </div>
  );
};

export default Credit;
